import * as React from "react";
import { Box } from "@dropbox/dig-foundations";
import classNames from "classnames";
import { SimpleTruncate, type SimpleTruncateProps } from "../simple-truncate";
import { Text } from "@dropbox/dig-components/typography";
import { useTileSize, useTileProps } from "./context";
import css from "./ActionLabel.module.css";
import type { TileSizes } from "./types";

type PolymorphicProps<E extends React.ElementType> = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<E> & {
    as?: E;
  }
>;

export type ActionLabelProps<T extends React.ElementType> =
  PolymorphicProps<T> & {
    onTruncateChange?: (isTruncated: boolean) => void;
    showTooltip?: boolean;
    maxLines?: number;

    children?: string;
  };

const getMaxTruncateLines = ({
  size,
  hasSubtext,
}: {
  size: TileSizes;
  hasSubtext: boolean;
}) => {
  if (hasSubtext) {
    return 1;
  }
  if (size === "large" || size === "medium") {
    return 2;
  } else {
    return 1;
  }
};

export const _ActionLabel = <T extends React.ElementType = "button">({
  as,
  children,
  className,
  maxLines,
  onTruncateChange,
  showTooltip,
  ...rest
}: ActionLabelProps<T>) => {
  const size = useTileSize({});
  const tileCtxProps = useTileProps();
  const PolymorphicComponent = as || "button";

  const truncateMaxLines =
    maxLines ??
    getMaxTruncateLines({
      size,
      hasSubtext: tileCtxProps.hasSubText,
    });

  const handleOnTruncateChange: SimpleTruncateProps["onOverflowChange"] =
    React.useCallback(
      ({ isOverflowing }) => {
        if (onTruncateChange) {
          onTruncateChange(isOverflowing);
        }
      },
      [onTruncateChange],
    );

  return (
    <Box
      as={Text}
      className={classNames(css.labelRoot, className)}
      size={size === "large" ? "medium" : "small"}
      variant="label"
      color="Text Base"
      display="block"
      isBold
    >
      <PolymorphicComponent {...rest} className={css.clicker}>
        <SimpleTruncate
          onOverflowChange={handleOnTruncateChange}
          lines={truncateMaxLines}
          tooltipControlProps={{ open: showTooltip }}
        >
          {children}
        </SimpleTruncate>
      </PolymorphicComponent>
    </Box>
  );
};
